import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, Radio } from 'lucide-react';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type TrafficResponse = {
  range: { from: string; to: string };
  retention: { rawFromDay: string; note: string };
  filters: { countries: string[] };
  kpis: { pageviewsTotal: number; uniqueVisitorsInRawWindow: number };
  series: { pageviewsByDay: Array<{ day: string; value: number }> };
  topCountries: Array<{ countryCode: string; pageviews: number }>;
};

type LiveResponse = {
  windowMinutes: number;
  pageviews: number;
  uniqueVisitors: number;
  computedAt: string;
};

type LoginAuthResponse = {
  range: { from: string; to: string };
  rows: Array<{ day: string; action: string; count: number }>;
};

const LOGIN_SUCCESS = new Set([
  'login.success',
  'login.success_mfa',
  'login.success_passkey',
]);

function mergeLoginRows(rows: LoginAuthResponse['rows']) {
  const map = new Map<string, { success: number; failed: number; other: number }>();
  for (const r of rows) {
    const cur = map.get(r.day) ?? { success: 0, failed: 0, other: 0 };
    if (LOGIN_SUCCESS.has(r.action)) cur.success += r.count;
    else if (r.action.includes('failed')) cur.failed += r.count;
    else cur.other += r.count;
    map.set(r.day, cur);
  }
  const days = Array.from(map.keys()).sort();
  return days.map((day) => ({
    day,
    success: map.get(day)!.success,
    failed: map.get(day)!.failed,
    other: map.get(day)!.other,
  }));
}

export default function StorefrontTrafficAnalyticsSection({
  from,
  to,
  fetcher,
}: {
  from: string;
  to: string;
  fetcher?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}) {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const request = fetcher ?? authorizedFetch;
  const [countryCode, setCountryCode] = React.useState('');
  const [city, setCity] = React.useState('');
  const [cityDraft, setCityDraft] = React.useState('');

  const [traffic, setTraffic] = React.useState<TrafficResponse | null>(null);
  const [loginAuth, setLoginAuth] = React.useState<LoginAuthResponse | null>(null);
  const [live, setLive] = React.useState<LiveResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', new Date(from).toISOString());
      if (to) qs.set('to', new Date(`${to}T23:59:59`).toISOString());
      if (countryCode.trim()) qs.set('countryCode', countryCode.trim());
      if (city.trim()) qs.set('city', city.trim());

      const qsLogin = new URLSearchParams();
      if (from) qsLogin.set('from', new Date(from).toISOString());
      if (to) qsLogin.set('to', new Date(`${to}T23:59:59`).toISOString());

      const [trRes, laRes] = await Promise.all([
        request(`${API_BASE}/stats/traffic?${qs.toString()}`),
        request(`${API_BASE}/stats/login-auth?${qsLogin.toString()}`),
      ]);

      const trBody = (await trRes.json().catch(() => null)) as any;
      const laBody = (await laRes.json().catch(() => null)) as any;
      if (!trRes.ok) throw new Error(trBody?.message || 'traffic');
      if (trBody?.ok === false) throw new Error(trBody?.message || 'Forbidden');
      if (!laRes.ok) throw new Error(laBody?.message || 'login-auth');
      if (laBody?.ok === false) throw new Error(laBody?.message || 'Forbidden');
      setTraffic(trBody as TrafficResponse);
      setLoginAuth(laBody as LoginAuthResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
      setTraffic(null);
      setLoginAuth(null);
    } finally {
      setLoading(false);
    }
  }, [city, from, request, to, countryCode]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const loadLive = React.useCallback(async () => {
    try {
      const res = await request(`${API_BASE}/stats/traffic/live`);
      const body = (await res.json().catch(() => null)) as any;
      if (!res.ok || body?.ok === false) return;
      setLive(body as LiveResponse);
    } catch {
      /* ignore */
    }
  }, [request]);

  React.useEffect(() => {
    void loadLive();
    const id = window.setInterval(() => void loadLive(), 10_000);
    return () => window.clearInterval(id);
  }, [loadLive]);

  const applyGeo = () => {
    setCity(cityDraft);
  };

  const loginRows = mergeLoginRows(loginAuth?.rows ?? []);

  return (
    <section className="space-y-6">
      <div>
        <div className="mb-2 flex items-center gap-2 text-brand-700">
          <Activity className="h-6 w-6" strokeWidth={2} />
          <h2 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
            {t('backoffice.analytics_traffic_title', 'Storefront traffic & sign-in')}
          </h2>
        </div>
        <p className="max-w-4xl workspace-page-subtitle text-slate-600">
          {t(
            'backoffice.analytics_traffic_subtitle',
            'Live traffic (last few minutes), daily page views with geo filters, and IAM login events from the audit log.',
          )}
        </p>
      </div>

      {live ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
              <Radio className="h-4 w-4 animate-pulse" />
              {t('backoffice.analytics_live_window', 'Live (last {{m}} min)', { m: live.windowMinutes })}
            </div>
            <div className="mt-4 flex flex-wrap gap-6">
              <div>
                <div className="text-[11px] font-semibold uppercase text-emerald-700">
                  {t('backoffice.analytics_live_pv', 'Page views')}
                </div>
                <div className="text-2xl font-semibold text-emerald-950">{live.pageviews}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase text-emerald-700">
                  {t('backoffice.analytics_live_uv', 'Unique visitors')}
                </div>
                <div className="text-2xl font-semibold text-emerald-950">{live.uniqueVisitors}</div>
              </div>
            </div>
            <div className="mt-2 text-[10px] text-emerald-800/80">
              {t('backoffice.analytics_live_updated', 'Updated {{t}}', {
                t: new Date(live.computedAt).toLocaleTimeString(),
              })}
            </div>
          </div>
        </div>
      ) : null}

      <div className="workspace-panel">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid w-full grid-cols-1 gap-3 sm:max-w-2xl sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('backoffice.analytics_traffic_country', 'Country code')}
              </label>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2"
              >
                <option value="">{t('backoffice.analytics_traffic_all_country', 'All countries')}</option>
                {(traffic?.filters.countries ?? []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('backoffice.analytics_traffic_city', 'City (exact match)')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cityDraft}
                  onChange={(e) => setCityDraft(e.target.value)}
                  placeholder={t('backoffice.analytics_traffic_city_ph', 'e.g. Berlin')}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2"
                />
                <button
                  type="button"
                  onClick={() => void applyGeo()}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                >
                  {t('backoffice.analytics_traffic_apply', 'Apply')}
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
          >
            {loading ? t('common.loading') : t('backoffice.analytics_refresh', 'Refresh')}
          </button>
        </div>

        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
        ) : null}

        {traffic?.retention?.note ? (
          <p className="mb-4 text-xs text-slate-500">{traffic.retention.note}</p>
        ) : null}

        {traffic?.kpis ? (
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase text-slate-500">
                {t('backoffice.analytics_traffic_pv_total', 'Page views (range)')}
              </div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">{traffic.kpis.pageviewsTotal}</div>
            </div>
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase text-slate-500">
                {t('backoffice.analytics_traffic_uv', 'Unique visitors (raw window)')}
              </div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">{traffic.kpis.uniqueVisitorsInRawWindow}</div>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="workspace-stat-card">
            <div className="mb-3 text-sm font-semibold text-slate-900">
              {t('backoffice.analytics_traffic_series', 'Daily page views')}
            </div>
            <div className="h-72">
              {traffic?.series?.pageviewsByDay?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={traffic.series.pageviewsByDay} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="value" name="Page views" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-500">{t('backoffice.analytics_empty', 'No analytics data yet.')}</p>
              )}
            </div>
          </div>

          <div className="workspace-stat-card">
            <div className="mb-3 text-sm font-semibold text-slate-900">
              {t('backoffice.analytics_traffic_top_countries', 'Top countries (range)')}
            </div>
            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
              {(traffic?.topCountries ?? []).slice(0, 10).map((r) => (
                <div key={r.countryCode} className="flex justify-between rounded-lg border border-slate-100 px-3 py-2 text-xs">
                  <span className="font-medium text-slate-700">{r.countryCode}</span>
                  <span className="text-slate-600">{r.pageviews}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="workspace-panel">
        <div className="mb-3 text-sm font-semibold text-slate-900">
          {t('backoffice.analytics_login_series', 'Auth / login events (IAM audit)')}
        </div>
        <div className="h-80">
          {loginRows.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={loginRows} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="success" name="Success" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
                <Bar dataKey="failed" name="Failed" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                <Bar dataKey="other" name="Other" stackId="a" fill="#94a3b8" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-500">{t('backoffice.analytics_empty', 'No analytics data yet.')}</p>
          )}
        </div>
      </div>
    </section>
  );
}
